// Calendário da semana: os 7 dias nas colunas, as horas do dia nas linhas
// (pedido do Lucas, 23/09/2026). Feito à mão — grade CSS + blocos em posição
// absoluta —, sem biblioteca de calendário nova no projeto.
//
// O que aparece:
//   · bloco colorido por lançamento COM horário (cor = contrato/centro);
//   · faixa "sem horário" no topo de cada dia, para o que foi lançado só
//     como quantidade — que continua valendo e não pode sumir da visão;
//   · linha vermelha do agora (relógio do SERVIDOR, não do computador);
//   · o cronômetro ligado como bloco tracejado que cresce até o agora.
// Como lançar: arrastar com o mouse numa faixa vazia (encaixa de 15 em 15);
// um clique simples propõe 1 h. No toque, arrastar é ROLAR a tela: tocar
// num horário vazio abre a janela já preenchida — nunca grava direto.
// Clicar num bloco abre a correção. Mover/esticar arrastando fica para depois.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Segmented, Select, Tag, Tooltip, Typography } from 'antd';
import { CoffeeOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import {
  chaveDestino, corDoDestino, diaDaSemana, faixaDoArraste, formatarHoras, formatarHorario,
  horarioParaMin, layoutDia, podeMarcarLocal, TIPOS_AUSENCIA,
  type Apontamento, type Cronometro, type Dia, type MarcaLocal,
} from './api';
import LocalDoDia from './LocalDoDia';
import TotalHoras from './TotalHoras';
import { algumTotalQuebrado } from './horasUtils';

const { Text } = Typography;
const PX_H = 48;                // pixels por hora
const ALTURA = 24 * PX_H;

type Arraste = { data: string; m0: number; m1: number };

function useLargura() {
  const [w, setW] = useState(() => (typeof window !== 'undefined' ? window.innerWidth : 1280));
  useEffect(() => {
    const f = () => setW(window.innerWidth);
    window.addEventListener('resize', f);
    return () => window.removeEventListener('resize', f);
  }, []);
  return w;
}

const rotuloDestino = (a: { contrato_numero: string | null; centro_custo_nome: string | null }) =>
  a.contrato_numero || a.centro_custo_nome || '—';

export default function CalendarioSemana({
  datas, apontamentos, dias, feriados, hoje, agora, cronometro, cronometroDeOutro, onCriar, onEditar, onLocal, onAcaoDia,
}: {
  datas: string[];
  apontamentos: Apontamento[];
  dias: Map<string, Dia>;
  /** Feriado do dia, por data (13/10/2026): nacional de data fixa ou cadastrado pela
   *  administração. O dia não é cobrado e NÃO fica bloqueado — quem trabalhar lança
   *  a hora normalmente e ela vale. */
  feriados?: Map<string, string>;
  hoje: string;
  agora: { data: string; minuto: number } | null;
  cronometro: Cronometro | null;
  cronometroDeOutro?: boolean;
  onCriar: (data: string, iniMin: number, fimMin: number) => void;
  onEditar: (a: Apontamento) => void;
  /** Marcar home office / campo do dia (30/09/2026). Sem ele, as caixas não aparecem. */
  onLocal?: (data: string, marca: MarcaLocal, valor: boolean) => void;
/** Marcar ausência e desfazê-la — vieram da grade quando ela saiu (30/09/2026).
   *  "Fechar o dia" não existe mais (01/10/2026): dia completo é o que tem as
   *  horas. Sem esta prop, o cabeçalho só mostra a situação do dia. */
  onAcaoDia?: (data: string, acao: 'reabrir' | 'ausencia', tipo?: string) => void;
}) {
  const largura = useLargura();
  const estreito = largura < 768;
  const [visao, setVisao] = useState<'dia' | '3' | '7'>(() => (estreito ? 'dia' : '7'));
  const [deslocamento, setDeslocamento] = useState(0);
  // O gesto em andamento mora numa REFERÊNCIA (lida na hora de soltar) e o
  // estado serve só para desenhar a prévia: soltar logo depois de encostar
  // não pode depender de a tela já ter se redesenhado.
  const [arraste, setArrasteEstado] = useState<Arraste | null>(null);
  const arrasteRef = useRef<Arraste | null>(null);
  const setArraste = (a: Arraste | null) => { arrasteRef.current = a; setArrasteEstado(a); };
  const toqueRef = useRef<{ data: string; x: number; y: number; min: number } | null>(null);
  const rolagem = useRef<HTMLDivElement>(null);

  // No celular, abre no dia de hoje (se for desta semana).
  useEffect(() => {
    if (!estreito) { setVisao('7'); setDeslocamento(0); return; }
    setVisao(v => (v === '7' ? 'dia' : v));
    const i = datas.indexOf(hoje);
    setDeslocamento(i >= 0 ? i : 0);
  }, [estreito, datas, hoje]);

  // Abre em 07:00 — ninguém quer ver a madrugada primeiro.
  useEffect(() => {
    if (rolagem.current) rolagem.current.scrollTop = 7 * PX_H - 8;
  }, []);

  const n = visao === '7' ? 7 : visao === '3' ? 3 : 1;
  const inicioVis = Math.min(Math.max(0, deslocamento), 7 - n);
  const visiveis = datas.slice(inicioVis, inicioVis + n);

  const porDia = useMemo(() => {
    const m = new Map<string, { comHora: Apontamento[]; semHora: Apontamento[]; total: number }>();
    datas.forEach(d => m.set(d, { comHora: [], semHora: [], total: 0 }));
    apontamentos.forEach(a => {
      const g = m.get(a.data);
      if (!g) return;
      g.total += a.minutos;
      if (a.hora_inicio && a.hora_fim) g.comHora.push(a); else g.semHora.push(a);
    });
    return m;
  }, [apontamentos, datas]);

  const bloqueado = (d: string) => d > hoje || dias.get(d)?.status === 'ausencia';

  const minutoDoPonteiro = (e: React.PointerEvent<HTMLDivElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return Math.max(0, Math.min(1440, ((e.clientY - r.top) / PX_H) * 60));
  };

  // Hoje pode ir além do agora (28/09): quem arrasta 14:00–18:00 às 17:50
  // lança o período que combinou, sem ser encurtado.
  function propor(data: string, ini: number, fim: number) {
    onCriar(data, ini, fim);
  }

  function aoPressionar(e: React.PointerEvent<HTMLDivElement>, data: string) {
    if (bloqueado(data)) return;
    if ((e.target as HTMLElement).closest('[data-bloco]')) return;
    const min = minutoDoPonteiro(e);
    if (e.pointerType === 'mouse') {
      if (e.button !== 0) return;
      // Captura para o arraste seguir mesmo se o mouse sair da coluna; se o
      // navegador recusar, o arraste funciona do mesmo jeito dentro dela.
      try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* sem captura */ }
      setArraste({ data, m0: min, m1: min });
    } else {
      toqueRef.current = { data, x: e.clientX, y: e.clientY, min };
    }
  }
  function aoMover(e: React.PointerEvent<HTMLDivElement>) {
    const a = arrasteRef.current;
    if (a) setArraste({ ...a, m1: minutoDoPonteiro(e) });
  }
  function aoSoltar(e: React.PointerEvent<HTMLDivElement>) {
    const arraste = arrasteRef.current;
    const toque = toqueRef.current;
    if (arraste) {
      const [a, b] = faixaDoArraste(arraste.m0, arraste.m1);
      // Clique sem arrastar: propõe 1 h a partir do quarto de hora clicado.
      if (Math.abs(arraste.m1 - arraste.m0) < 8) propor(arraste.data, a, Math.min(1440, a + 60));
      else propor(arraste.data, a, b);
      setArraste(null);
      return;
    }
    if (toque) {
      // Toque que andou é rolagem, não pedido de lançamento.
      const andou = Math.hypot(e.clientX - toque.x, e.clientY - toque.y) > 8;
      if (!andou) {
        const a = Math.floor(toque.min / 15) * 15;
        propor(toque.data, a, Math.min(1440, a + 60));
      }
      toqueRef.current = null;
    }
  }

  const previa = arraste ? faixaDoArraste(arraste.m0, arraste.m1) : null;
  const cols = `52px repeat(${visiveis.length}, minmax(0, 1fr))`;

  // Trecho do cronômetro em cada dia visível (de quando começou até agora).
  const trechoCron = (d: string): [number, number] | null => {
    if (!cronometro || !agora) return null;
    if (d < cronometro.data_inicio || d > agora.data) return null;
    const ini = d === cronometro.data_inicio ? (horarioParaMin(cronometro.hora_inicio) ?? 0) : 0;
    const fim = d === agora.data ? agora.minuto : 1440;
    return fim > ini ? [ini, fim] : null;
  };

  return (
    <div>
      {estreito && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
          <Segmented size="small" value={visao} onChange={(v) => setVisao(v as any)}
            options={[{ label: 'Dia', value: 'dia' }, { label: '3 dias', value: '3' }, { label: 'Semana', value: '7' }]} />
          {n < 7 && (
            <span>
              <Button size="small" icon={<LeftOutlined />} disabled={inicioVis <= 0}
                onClick={() => setDeslocamento(inicioVis - 1)} />
              <Button size="small" icon={<RightOutlined />} disabled={inicioVis + n >= 7} style={{ marginLeft: 4 }}
                onClick={() => setDeslocamento(inicioVis + 1)} />
            </span>
          )}
        </div>
      )}

      <div style={{ border: '1px solid #E6E8F0', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
        {/* Cabeçalho: dia, total e situação */}
        <div style={{ display: 'grid', gridTemplateColumns: cols, borderBottom: '1px solid #E6E8F0', background: '#FAFBFD' }}>
          <div />
          {visiveis.map(d => {
            // Mesma altura em todos os dias visíveis: o total em decimal só
            // ocupa a segunda linha quando algum deles tem minuto quebrado.
            const reservarDecimal = algumTotalQuebrado(visiveis.map(v => porDia.get(v)?.total || 0));
            const dia = dias.get(d);
            const tot = porDia.get(d)?.total || 0;
            const ehHoje = d === hoje;
            const feriado = feriados?.get(d) || null;
            return (
              <div key={d} style={{ padding: '6px 4px', textAlign: 'center', borderLeft: '1px solid #EEF0F5', lineHeight: 1.3 }}>
                <div style={{ fontWeight: ehHoje ? 700 : 500, color: ehHoje ? '#0930D0' : undefined }}>
                  {diaDaSemana(d)} {d.slice(8, 10)}/{d.slice(5, 7)}
                </div>
                <div style={{ fontSize: 11, color: tot ? '#333' : '#aaa' }}>
                  <TotalHoras minutos={tot} vazio="—" reservar={reservarDecimal} tamanhoDecimal={10} />
                </div>
                {dia?.status === 'ausencia' && (
                  <Tag color="blue" style={{ marginInline: 0, fontSize: 10 }}>
                    {TIPOS_AUSENCIA.find(t => t.value === dia.tipo_ausencia)?.label || dia.tipo_ausencia}
                  </Tag>
                )}
                {/* Feriado (13/10/2026): o dia não é cobrado e ninguém precisa marcar ausência.
                    Quem trabalhou lança a hora normalmente — a etiqueta fica, a hora vale. */}
                {feriado && dia?.status !== 'ausencia' && (
                  <Tooltip title={tot > 0
                    ? `Feriado: ${feriado}. As horas lançadas valem normalmente.`
                    : `Feriado: ${feriado}. Não precisa anotar nem marcar ausência — se trabalhar, é só lançar a hora.`}>
                    {/* "Feriado" vem primeiro: em coluna estreita o nome é que encurta, não a palavra que explica o dia. */}
                    <Tag color="gold" style={{ marginInline: 0, fontSize: 10, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'bottom' }}>
                      Feriado: {feriado}
                    </Tag>
                  </Tooltip>
                )}
                {/* Ausência: só em dia útil sem hora lançada (o servidor recusa com hora
                    dentro); marcada, o mesmo lugar oferece desfazer. Vale também para
                    dia FUTURO (13/10/2026: férias da semana que vem) — o caminho com
                    nome é o botão "Folga ou férias", que marca o período inteiro. */}
                {onAcaoDia && dia?.status !== 'ausencia' && tot <= 0 && datas.indexOf(d) < 5 && !feriado && (
                  <div>
                    <Tooltip title={d > hoje ? 'Não vou trabalhar neste dia: marcar folga, férias…' : 'Não trabalhei neste dia: marcar ausência (férias, atestado, folga…)'}>
                      <Select
                        size="small" variant="borderless" style={{ width: 34 }} value={undefined}
                        suffixIcon={<CoffeeOutlined />} popupMatchSelectWidth={140}
                        showSearch optionFilterProp="label"
                        options={TIPOS_AUSENCIA}
                        onChange={(v) => onAcaoDia(d, 'ausencia', v)}
                      />
                    </Tooltip>
                  </div>
                )}
                {onAcaoDia && dia?.status === 'ausencia' && (
                  <div>
                    <Button size="small" type="link" style={{ padding: 0, fontSize: 11, height: 18 }}
                      onClick={() => onAcaoDia(d, 'reabrir')}>desfazer</Button>
                  </div>
                )}
                {onLocal && podeMarcarLocal(d, hoje, dia?.status) && (
                  <div style={{ marginTop: 2 }}>
                    <LocalDoDia remoto={!!dia?.remoto} campo={!!dia?.campo} emLinha={n === 1}
                      onMudar={(marca, valor) => onLocal(d, marca, valor)} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Faixa "sem horário": o que foi lançado só como quantidade */}
        <div style={{ display: 'grid', gridTemplateColumns: cols, borderBottom: '1px solid #E6E8F0' }}>
          <div style={{ fontSize: 10, color: '#888', padding: '4px 2px', textAlign: 'right', lineHeight: 1.2 }}>sem<br />horário</div>
          {visiveis.map(d => (
            <div key={d} style={{ borderLeft: '1px solid #EEF0F5', padding: 3, minHeight: 28, maxHeight: 84, overflowY: 'auto' }}>
              {(porDia.get(d)?.semHora || []).map(a => {
                const cor = corDoDestino(chaveDestino(a));
                return (
                  <Tooltip key={a.id} title={`${rotuloDestino(a)} · ${formatarHoras(a.minutos)}${a.descricao ? ` — ${a.descricao}` : ''}`}>
                    <div
                      onClick={() => onEditar(a)}
                      style={{
                        background: cor.fundo, borderLeft: `3px solid ${cor.borda}`, borderRadius: 4,
                        fontSize: 11, padding: '1px 5px', marginBottom: 2, cursor: 'pointer',
                        whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                      }}>
                      <strong>{rotuloDestino(a)}</strong> · {formatarHoras(a.minutos)}
                    </div>
                  </Tooltip>
                );
              })}
            </div>
          ))}
        </div>

        {/* Grade das 24 horas */}
        <div ref={rolagem} style={{ height: 'clamp(360px, calc(100vh - 400px), 900px)', overflowY: 'auto', position: 'relative' }}>
          <div style={{ display: 'grid', gridTemplateColumns: cols, height: ALTURA, position: 'relative' }}>
            <div style={{ position: 'relative' }}>
              {Array.from({ length: 24 }, (_, h) => (
                <div key={h} style={{ position: 'absolute', top: h * PX_H - 7, right: 6, fontSize: 10, color: '#999' }}>
                  {h ? `${String(h).padStart(2, '0')}:00` : ''}
                </div>
              ))}
            </div>
            {visiveis.map(d => {
              const g = porDia.get(d)!;
              const blocos = layoutDia(g.comHora.map(a => ({
                id: a.id, inicio: horarioParaMin(a.hora_inicio) ?? 0, fim: horarioParaMin(a.hora_fim) ?? 0, a,
              })));
              const aus = dias.get(d)?.status === 'ausencia';
              const futuro = d > hoje;
              const cron = trechoCron(d);
              return (
                <div
                  key={d}
                  onPointerDown={(e) => aoPressionar(e, d)}
                  onPointerMove={aoMover}
                  onPointerUp={aoSoltar}
                  onPointerCancel={() => { setArraste(null); toqueRef.current = null; }}
                  style={{
                    position: 'relative', borderLeft: '1px solid #EEF0F5',
                    touchAction: 'pan-y', userSelect: 'none',
                    cursor: bloqueado(d) ? 'default' : 'crosshair',
                    backgroundColor: futuro ? '#FAFAFA' : aus ? '#F4F7FF' : undefined,
                    backgroundImage: 'repeating-linear-gradient(to bottom, #EEF0F5 0, #EEF0F5 1px, transparent 1px, transparent 48px)',
                  }}>
                  {blocos.map(b => {
                    const a = (b as any).a as Apontamento;
                    const cor = corDoDestino(chaveDestino(a));
                    const alt = Math.max(18, ((b.fim - b.inicio) / 60) * PX_H - 2);
                    // O que a pessoa escreveu é mais específico que o nome da lista.
                    const texto = a.descricao || a.atividade_tipo_nome;
                    return (
                      <Tooltip key={b.id} title={`${rotuloDestino(a)} · ${a.hora_inicio}–${a.hora_fim}${texto ? ` · ${texto}` : ''}${a.origem === 'timer' ? ' (cronômetro)' : ''}`}>
                        <div
                          data-bloco
                          onClick={() => onEditar(a)}
                          style={{
                            position: 'absolute', top: (b.inicio / 60) * PX_H + 1, height: alt,
                            left: `calc(${(b.coluna / b.colunas) * 100}% + 2px)`, width: `calc(${100 / b.colunas}% - 4px)`,
                            background: cor.fundo, borderLeft: `3px solid ${cor.borda}`, borderRadius: 4,
                            padding: '2px 5px', fontSize: 11, lineHeight: 1.25, overflow: 'hidden', cursor: 'pointer',
                            boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                          }}>
                          <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {a.origem === 'timer' ? '⏱ ' : ''}{rotuloDestino(a)}
                          </div>
                          {alt > 30 && texto && (
                            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#444' }}>{texto}</div>
                          )}
                          {alt > 44 && <div style={{ color: '#666' }}>{a.hora_inicio}–{a.hora_fim}</div>}
                        </div>
                      </Tooltip>
                    );
                  })}

                  {cron && cronometro && (
                    <Tooltip title={cronometroDeOutro
                      ? `Cronômetro desta pessoa ligado desde ${cronometro.hora_inicio}`
                      : 'Cronômetro ligado — pare ou ajuste pelo relógio no topo da tela'}>
                      <div data-bloco style={{
                        position: 'absolute', top: (cron[0] / 60) * PX_H + 1,
                        height: Math.max(14, ((cron[1] - cron[0]) / 60) * PX_H - 2),
                        left: 2, right: 2, border: '2px dashed #D93025', borderRadius: 4,
                        background: 'rgba(217,48,37,0.06)', fontSize: 11, padding: '1px 5px', color: '#B3261E',
                        overflow: 'hidden', whiteSpace: 'nowrap',
                      }}>
                        ⏱ {rotuloDestino(cronometro)} · rodando
                      </div>
                    </Tooltip>
                  )}

                  {agora && agora.data === d && (
                    <div style={{ position: 'absolute', left: 0, right: 0, top: (agora.minuto / 60) * PX_H, height: 0,
                                  borderTop: '2px solid #D93025', pointerEvents: 'none' }}>
                      <span style={{ position: 'absolute', left: -4, top: -5, width: 8, height: 8, borderRadius: '50%', background: '#D93025' }} />
                    </div>
                  )}

                  {previa && arraste && arraste.data === d && (
                    <div style={{
                      position: 'absolute', top: (previa[0] / 60) * PX_H, height: ((previa[1] - previa[0]) / 60) * PX_H,
                      left: 2, right: 2, border: '2px dashed #0930D0', background: 'rgba(9,48,208,0.08)', borderRadius: 4,
                      fontSize: 11, padding: '1px 5px', color: '#0930D0', pointerEvents: 'none',
                    }}>
                      {formatarHorario(previa[0])}–{formatarHorario(previa[1])}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <Text type="secondary" style={{ display: 'block', marginTop: 6, fontSize: 12 }}>
        {estreito
          ? 'Toque num horário vazio para lançar; toque num bloco para corrigir.'
          : 'Arraste numa faixa vazia para lançar (ou clique para 1 h); clique num bloco para corrigir.'}
      </Text>
    </div>
  );
}
